import { useMemo, useState } from "react";
import SectionHeading from "./SectionHeading";
import { hotShows } from "@/mocks/shows";

const allLabel = "全部";

export default function HotShows() {
  const [active, setActive] = useState(allLabel);

  const tabs = useMemo(() => {
    const set = new Set(hotShows.map((show) => show.genre));
    return [allLabel, ...Array.from(set)];
  }, []);

  const list = useMemo(
    () => (active === allLabel ? hotShows : hotShows.filter((s) => s.genre === active)),
    [active]
  );

  const [followed, setFollowed] = useState<string[]>([]);

  const toggleFollow = (id: string) => {
    setFollowed((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  return (
    <section id="hot" className="scroll-mt-24 bg-background-100/60 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <SectionHeading
            eyebrow="热播推荐"
            title="近期讨论度最高的六部剧集"
            description="按题材切换可以快速缩小范围。卡片上的评分、集数与简介都来自页面整理，用来帮助判断哪一部更适合先看。"
          />
          <div className="reveal flex flex-wrap gap-2">
            {tabs.map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => setActive(tab)}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                  active === tab
                    ? "bg-primary-500 text-background-50"
                    : "border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-9 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {list.map((show) => {
            const isFollowed = followed.includes(show.id);
            return (
              <article
                key={show.id}
                className="reveal group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50"
              >
                <div className="relative h-64 w-full overflow-hidden bg-background-200">
                  <img
                    src={show.cover}
                    alt={`${show.title}剧集封面`}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute left-3 top-3 rounded-full bg-primary-500 px-2.5 py-1 text-[11px] font-medium text-background-50">
                    {show.genre}
                  </span>
                  <span className="absolute right-3 top-3 flex items-center gap-1 rounded-full bg-background-50/90 px-2.5 py-1 text-[11px] font-semibold text-accent-700">
                    <i className="ri-star-fill text-accent-500"></i>
                    {show.rating}
                  </span>
                  <span
                    className={`absolute bottom-3 left-3 rounded-full px-2.5 py-1 text-[11px] font-medium ${
                      show.status === "已完结"
                        ? "bg-accent-500 text-foreground-950"
                        : "bg-secondary-100 text-secondary-900"
                    }`}
                  >
                    {show.status}
                  </span>
                </div>

                <div className="flex flex-1 flex-col p-5">
                  <div className="flex items-center justify-between gap-3">
                    <h3 className="font-heading text-lg font-bold text-foreground-950">
                      {show.title}
                    </h3>
                    <span className="whitespace-nowrap text-xs text-foreground-500">
                      {show.year}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-foreground-500">{show.episodes}</p>
                  <p className="mt-3 flex-1 text-sm leading-7 text-foreground-600">
                    {show.summary}
                  </p>

                  <div className="mt-4 flex flex-wrap gap-2">
                    {show.tags.map((tag) => (
                      <span
                        key={tag}
                        className="rounded-full bg-secondary-100/80 px-2.5 py-1 text-[11px] text-secondary-900"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  <button
                    type="button"
                    onClick={() => toggleFollow(show.id)}
                    className={`mt-5 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-2.5 text-sm font-medium transition-colors ${
                      isFollowed
                        ? "bg-accent-500 text-foreground-950 hover:bg-accent-600"
                        : "bg-primary-500 text-background-50 hover:bg-primary-600"
                    }`}
                  >
                    <i className={isFollowed ? "ri-bookmark-fill" : "ri-bookmark-line"}></i>
                    {isFollowed ? "已加入追剧清单" : "加入追剧清单"}
                  </button>
                </div>
              </article>
            );
          })}
        </div>

        {list.length === 0 ? (
          <p className="mt-9 rounded-lg border border-background-200 bg-background-50 p-6 text-center text-sm text-foreground-500">
            该题材下暂时没有推荐剧集，切换其他标签再看看。
          </p>
        ) : null}
      </div>
    </section>
  );
}